import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Autocomplete from 'react-google-autocomplete';
import { useLoadScript } from '@react-google-maps/api';
import './LocationSelector.css';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDtL9gUclA-9z4rLEjHgS5sH3P_PWikENQ';

const libraries = ['places'];

const LocationSelector = ({
  onLocationSelect,
  initialCity = '',
  initialRegion = '',
  initialCountry = '',
  touched = {},
  errors = {}
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Use a ref to track if this is the initial render
  const isInitialMount = useRef(true);

  // Use a ref to track if the location was manually selected
  const locationManuallySelected = useRef(false);

  const [locationData, setLocationData] = useState({
    city: initialCity,
    region: initialRegion,
    country: initialCountry,
    latitude: 0,
    longitude: 0,
  });

  // Update locationData if initialCity/Region/Country props change
  useEffect(() => {
    // Only update if the values are different to avoid infinite loops
    if (
      initialCity !== locationData.city ||
      initialRegion !== locationData.region ||
      initialCountry !== locationData.country
    ) {
      setLocationData(prevData => ({
        ...prevData,
        city: initialCity,
        region: initialRegion,
        country: initialCountry
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCity, initialRegion, initialCountry]);

  // Only call onLocationSelect when the location data changes AND it's not the initial render
  // OR when the location was manually selected
  useEffect(() => {
    if (onLocationSelect && (!isInitialMount.current || locationManuallySelected.current)) {
      onLocationSelect(locationData);
      locationManuallySelected.current = false;
    }

    // After the first render, set isInitialMount to false
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationData]);

  const handlePlaceSelect = (place) => {
    if (!place || !place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    let city = '';
    let region = '';
    let country = '';

    // Extract address components
    if (place.address_components) {
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'locality':
            city = component.long_name;
            break;
          case 'administrative_area_level_1':
            region = component.long_name;
            break;
          case 'country':
            country = component.long_name;
            break;
          default:
            break;
        }
      }
    }

    // If city is not found, try to use the place name
    if (!city && place.name) {
      city = place.name;
    }

    // Set the flag to indicate this was a manual selection
    locationManuallySelected.current = true;

    setLocationData({
      city,
      region,
      country,
      latitude: lat,
      longitude: lng,
    });
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  // Handle key down events in the location input
  const handleKeyDown = (event) => {
    // Prevent form submission when Enter key is pressed in the location field
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <div className="location-selector-container">
      <Row>
        <Col xs={12}>
          <Form.Group className="mb-3">
            <Form.Label>Location</Form.Label>
            <Autocomplete
              className={`form-control ${touched.city && errors.city ? 'is-invalid' : ''} ${locationData.city && locationData.region && locationData.country ? 'is-valid' : ''}`}
              apiKey={GOOGLE_MAPS_API_KEY}
              onPlaceSelected={handlePlaceSelect}
              options={{
                types: ['locality'],
                componentRestrictions: { country: [] }, // No country restriction
              }}
              defaultValue={initialCity ? `${initialCity}, ${initialRegion}, ${initialCountry}` : ''}
              placeholder="Search for your city"
              onKeyDown={handleKeyDown}
            />
            {touched.city && errors.city && (
              <div className="invalid-feedback d-block">
                {errors.city}
              </div>
            )}
            <Form.Text className="location-help-text">
              Search for your city to automatically fill location details
            </Form.Text>

            {/* Display validation errors for region and country if needed */}
            {touched.region && errors.region && (
              <div className="invalid-feedback d-block mt-1">
                {errors.region}
              </div>
            )}
            {touched.country && errors.country && (
              <div className="invalid-feedback d-block mt-1">
                {errors.country}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Hidden fields that still hold the values for backend submission */}
      <div style={{ display: 'none' }}>
        <input
          type="hidden"
          name="city"
          value={locationData.city}
        />
        <input
          type="hidden"
          name="region"
          value={locationData.region}
        />
        <input
          type="hidden"
          name="country"
          value={locationData.country}
        />
        <input
          type="hidden"
          name="latitude"
          value={locationData.latitude.toString()}
        />
        <input
          type="hidden"
          name="longitude"
          value={locationData.longitude.toString()}
        />
      </div>
    </div>
  );
};

export default LocationSelector;
